import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { useEffect, useRef, useState } from "react";
import { flagHelp, flagText, galText, OFF_SEASON_HELP, STATUS_HELP, STATUS_TEXT } from "./accounts";
import { CardLabel, Hint } from "./Hint";
import * as api from "./api";
import { SourceTag } from "./SourceTag";
import { earlyPullTitle, earlyPullVisitText, weekDaysText } from "./calendar";
import { callDay } from "./calls";
import { pickedUpText, routesDay, truckDaysText, weekFromSearch, type WeekTab, weekTabs, weekText, withChoice } from "./cycle";
import { go, useTitle } from "./hooks";
import { todayIn } from "./pickup";
import { saveFailure, SERVER_PROBLEM } from "./save";
import { blackoutText, seasonText } from "./seasons";
import { AREA_COLUMNS, STOP_COLUMNS } from "./columns";
import { type SortColumn, sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { WeekTabs } from "./WeekTabs";
import { cycleKey } from "./store/pageViews";
import { loadFailure, useView, ViewFreshness } from "./store/useView";
import { Skeleton } from "./Skeleton";

type BoardLoad = api.CycleBoard | "session" | "server" | "network" | undefined;

const cardLabel = <T,>(columns: readonly SortColumn<T>[], key: string, label?: string) => (
  <CardLabel column={columns.find((column) => column.key === key)} label={label} />
);

function choiceText(stop: api.CycleStop): string {
  if (stop.status === "HOLD") return "Held";
  return stop.selected ? "On the board" : "Off the board";
}

function CapacityCheck({ found, title }: { found: api.CycleCapacity; title: string }) {
  return (
    <section aria-labelledby="cycle-capacity" className="cycle-capacity" data-testid="cycle-capacity">
      <h2 id="cycle-capacity">{title}</h2>
      <dl className="facts">
        <dt>Stops</dt>
        <dd data-testid="cycle-stops">{found.stops}</dd>
        <dt>Gallons</dt>
        <dd>
          {galText(found.gallons)} <span className="chip-flag">Estimated</span>
        </dd>
        <dt>Gallons for each stop</dt>
        <dd>{galText(found.gal_per_stop)}</dd>
        <dt>Hours at 1.75 stops an hour</dt>
        <dd>{found.hours} h</dd>
        <dt>Truck-days of 8 hours</dt>
        <dd data-testid="cycle-truck-days">{truckDaysText(found.truck_days)}</dd>
        <dt>Gallons for each truck-day</dt>
        <dd>{galText(found.gal_per_truck_day)}</dd>
      </dl>
      {found.trucks.length === 0 && (
        <p className="sub" data-testid="cycle-no-trucks">
          No active trucks. Add them in Manage, Fleet.
        </p>
      )}
      <ul className="cycle-trucks" aria-label="Truck sizes">
        {found.trucks.map((truck) => (
          <li key={truck.size_gal} className={truck.fits ? undefined : "error"}>
            {galText(truck.size_gal)} truck: {truck.fits ? "holds a truck-day" : "too small for a truck-day"}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CyclePage({
  base,
  org,
  auth,
  user,
  canView,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
}) {
  const [week, setWeekState] = useState<number | undefined>(() => weekFromSearch(location.search));
  const [tabs, setTabs] = useState<WeekTab[] | null>(null);
  const [status, setStatus] = useState<{ ok: boolean; text: string; session?: boolean } | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [onlyEarly, setOnlyEarlyState] = useState(() => new URLSearchParams(location.search).get("early") === "1");
  const live = useRef(true);
  const [areaSort, setAreaSort] = useSort("areas", AREA_COLUMNS);
  const [sort, setSort] = useSort("sort", STOP_COLUMNS);
  useTitle(`Cycle board · ${org.name}`);
  const setWeek = (next: number) => {
    setWeekState(next);
    const params = new URLSearchParams(location.search);
    params.set("week", String(next));
    history.replaceState(history.state, "", `${location.pathname}?${params.toString()}${location.hash}`);
  };
  const setOnlyEarly = (next: boolean) => {
    setOnlyEarlyState(next);
    const params = new URLSearchParams(location.search);
    if (next) params.set("early", "1");
    else params.delete("early");
    const query = params.toString();
    history.replaceState(history.state, "", `${location.pathname}${query ? `?${query}` : ""}${location.hash}`);
  };
  const view = useView(canView === true ? cycleKey(week) : null, async () => api.cycleBoard(await user.getIdToken(), week));
  const found: BoardLoad = view.data ?? (view.error ? loadFailure(view.error) : undefined);
  const setFound = view.keep;
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  useEffect(() => {
    if (view.data) setTabs(weekTabs(view.data));
  }, [view.data]);
  if (canView === false) {
    return (
      <main data-testid="cycle-hidden">
        <h1 tabIndex={-1}>Cycle board</h1>
        <p>Your role does not show the cycle board.</p>
      </main>
    );
  }
  const board = typeof found === "object" ? found : undefined;
  const choose = async (stop: api.CycleStop, selected: boolean) => {
    if (!board || saving !== null) return;
    const before = board;
    setFound(withChoice(board, stop.account_id, selected));
    setSaving(stop.account_id);
    setStatus(null);
    try {
      const choice = { id: crypto.randomUUID(), account_id: stop.account_id, week_start: board.week_start, selected };
      const next = await api.chooseStop(await user.getIdToken(), choice);
      if (!live.current) return;
      setFound(next);
      setStatus({ ok: true, text: `Saved: ${stop.number} is ${selected ? "on" : "off"} the board.` });
    } catch (e) {
      if (!live.current) return;
      setFound(before);
      const failed = saveFailure(e, "The choice");
      setStatus({ ok: false, text: failed.text, session: !failed.ok && failed.session });
    } finally {
      setSaving(null);
    }
  };
  const download = async () => {
    if (!board) return;
    try {
      const text = await api.cycleCsv(await user.getIdToken(), board.week);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
      link.download = `stops-week-${board.week}-${board.week_start}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      setStatus({ ok: false, text: "The file was not made. Try again." });
    }
  };
  const shownStops = board ? board.stops.filter((stop) => !onlyEarly || stop.early_pull) : [];
  const earlyCount = board ? board.stops.filter((stop) => stop.early_pull).length : 0;
  const areas = [...new Set(shownStops.map((stop) => stop.area))];
  const loadSource = (stop: api.CycleStop) =>
    stop.picked_up
      ? "Emptied this week"
      : stop.from_estimate
        ? "From the fill estimate"
        : stop.from_pickups
          ? "From the pickups"
          : "From the stated rate";
  const stopRow = (stop: api.CycleStop) => (
    <tr
      key={stop.account_id}
      data-testid="cycle-stop"
      data-number={stop.number}
      data-selected={stop.selected}
      data-chosen-day={stop.chosen_at ? callDay(stop.chosen_at, org.time_zone) : ""}
      data-picked-up={stop.picked_up ?? ""}
      aria-busy={saving === stop.account_id || undefined}
      className={stop.selected ? undefined : "unselected"}
    >
      <td className="visit" data-label="">
        <input
          type="checkbox"
          data-testid="cycle-choose"
          checked={stop.selected}
          disabled={!stop.can_choose}
          aria-label={`Visit ${stop.number} ${stop.name} in week ${board?.week}`}
          onChange={(e) => void choose(stop, e.target.checked)}
        />
        {!stop.can_choose && <span className="sub choice-state">{choiceText(stop)}</span>}
        {stop.off_season && (
          <Hint text={`${OFF_SEASON_HELP} Season ${seasonText(stop.season)}.`} stop={false}>
            <span className="chip-flag" data-testid="cycle-off-season">
              Off season
            </span>
          </Hint>
        )}
      </td>
      <th scope="row">
        <a href={`${base}/accounts/${stop.account_id}`} onClick={go} title={stop.address}>
          <span className="account-number">{stop.number}</span> {stop.name}
        </a>{" "}
        <SourceTag tag={stop.data_source} stop={false} />
        <span className="sub desk-hide">{stop.address}</span>
        {stop.picked_up && (
          <span className="sub picked-up" data-testid="cycle-picked-up">
            <Hint text="It is off the board unless you choose it." stop={false}>
              {pickedUpText(stop)}
            </Hint>
            <span className="desk-hide">. It is off the board unless you choose it.</span>
          </span>
        )}
      </th>
      <td data-label="Area" data-help>
        {cardLabel(STOP_COLUMNS, "area")}
        {stop.area}
      </td>
      <td data-label="Status" data-help>
        {cardLabel(STOP_COLUMNS, "status")}
        <Hint text={STATUS_HELP[stop.status]} stop={false}>
          {STATUS_TEXT[stop.status]}
        </Hint>
        <span className="sub">
          Weeks {stop.week_group}
          {stop.added && !stop.early_pull ? ", added" : ""}
        </span>
      </td>
      <td className="num" data-label="Load" data-help>
        {cardLabel(STOP_COLUMNS, "load", "Load")}
        {stop.capacity_gal > 0 ? (
          <>
            <Hint text={loadSource(stop)} stop={false}>
              {`${galText(Math.min(stop.estimated_gal, stop.capacity_gal))} of ${galText(stop.capacity_gal)}`}
            </Hint>
            <span className="sub desk-hide">{loadSource(stop)}</span>
          </>
        ) : (
          "–"
        )}
      </td>
      <td className="num" data-label="Full" data-help>
        {cardLabel(STOP_COLUMNS, "share")}
        {stop.load_share === null || !(stop.capacity_gal > 0) ? "–" : `${Math.round(Math.min(stop.load_share, 1) * 100)}%`}
      </td>
      <td className="num" data-label="Weeks left" data-help>
        {cardLabel(STOP_COLUMNS, "left")}
        {stop.weeks_left ?? "–"}
      </td>
      <td className="flags" data-label="">
        {stop.early_pull && (
          <Hint text={`${earlyPullTitle(stop, board?.early_margin ?? 0.1, board?.early_risk)}. The planner suggests an earlier visit.`} stop={false}>
            <span className="chip-flag serious" data-testid="cycle-early-pull">
              Early pull
            </span>
          </Hint>
        )}
        {stop.early_pull && earlyPullVisitText(stop) && (
          <span className="sub below" data-testid="cycle-early-visit">
            {earlyPullVisitText(stop)}
          </span>
        )}
        {stop.blackouts && stop.blackouts.length > 0 && (
          <Hint text={blackoutText(stop.blackouts)} stop={false}>
            <span className="chip-flag" data-testid="cycle-blackouts">
              Blackouts ({stop.blackouts.length})<span className="visually-hidden">: {blackoutText(stop.blackouts)}</span>
            </span>
          </Hint>
        )}
        {stop.flags.map((flag) => (
          <Hint key={flag} text={flagHelp(stop, flag)} stop={false}>
            <span className={flag === "near_full" ? "chip-flag serious" : "chip-flag"} data-flag={flag}>
              {flagText(stop, flag)}
            </span>
          </Hint>
        ))}
      </td>
    </tr>
  );
  return (
    <main className="issues-page cycle-page wide" data-testid="cycle-page" data-today={todayIn(org.time_zone)}>
      <h1 tabIndex={-1}>Cycle board</h1>
      {tabs && <WeekTabs tabs={tabs} week={board?.week ?? week} onPick={setWeek} testId="cycle-week" />}
      <ViewFreshness view={view} timeZone={org.time_zone} auth={auth} />
      {found === undefined ? (
        <Skeleton shape="table" columns={6} rows={8} />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => void signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={view.reload}>
            Try again
          </button>
        </p>
      ) : (
        <>
          <p className="sub" data-testid="cycle-week" data-week={found.week} data-start={found.week_start}>
            {weekText(found)}
            {found.week === found.this_week ? " (this week)" : ""}
          </p>
          {(found.visit_day || (found.holidays ?? []).length > 0) && (
            <p className="sub" data-testid="cycle-week-days" data-visit-day={found.visit_day ?? ""}>
              {weekDaysText(found.visit_day, found.holidays ?? [])}
            </p>
          )}
          {!found.can_choose && (
            <p data-testid="cycle-view-only">You can see the board. A dispatcher chooses the stops.</p>
          )}
          <p role="status" data-testid="cycle-status" className={status && !status.ok ? "error" : undefined}>
            {status?.text}
            {status?.session && (
              <>
                {" "}
                <button type="button" onClick={() => void signOut(auth)}>
                  Sign in again
                </button>
              </>
            )}
          </p>
          <CapacityCheck found={found.capacity} title={`Week ${found.week} total (selected stops)`} />
          <p className="actions">
            <button type="button" data-testid="cycle-csv" onClick={() => void download()} disabled={found.capacity.stops === 0}>
              Export stops
            </button>
            {found.can_choose && (
              <>
                {" "}
                <a
                  className="button"
                  data-testid="cycle-make-routes"
                  href={`${base}/routes?day=${routesDay(found.week_start, todayIn(org.time_zone))}`}
                  onClick={go}
                >
                  Make routes for this week
                </a>
              </>
            )}
          </p>
          {found.stops.length === 0 ? (
            <p data-testid="cycle-none">No accounts are planned for this week.</p>
          ) : (
            <>
              <section aria-labelledby="cycle-areas">
                <h2 id="cycle-areas">Areas</h2>
                <div className="table-scroll" tabIndex={0} role="region" aria-label="Areas of the week">
                  <table className="cycle-areas list" data-testid="cycle-areas">
                    <thead>
                      <tr>
                        {AREA_COLUMNS.map((column) => (
                          <SortTh key={column.key} column={column} sort={areaSort} onSort={setAreaSort} />
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortRows(found.areas, AREA_COLUMNS, areaSort).map((area) => (
                        <tr key={area.area}>
                          <th scope="row">{area.area}</th>
                          <td className="num" data-label="Stops" data-help>
                            {cardLabel(AREA_COLUMNS, "stops")}
                            {area.capacity.stops} of {area.listed}
                          </td>
                          <td className="num" data-label="Estimated gal" data-help>
                            {cardLabel(AREA_COLUMNS, "gal")}
                            {galText(area.capacity.gallons)}
                          </td>
                          <td className="num" data-label="Truck-days of 8 h" data-help>
                            {cardLabel(AREA_COLUMNS, "days")}
                            {area.capacity.truck_days}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
              <section aria-labelledby="cycle-accounts">
                <h2 id="cycle-accounts">Accounts</h2>
                <label className="check">
                  <input
                    type="checkbox"
                    data-testid="cycle-early-filter"
                    checked={onlyEarly}
                    onChange={(e) => setOnlyEarly(e.target.checked)}
                  />
                  Only early pulls ({earlyCount})
                </label>
                <SortSelect id="cycle-sort" columns={STOP_COLUMNS} sort={sort} onSort={setSort} fallback="By area" />
                <div className="table-scroll" tabIndex={0} role="region" aria-label="Accounts of the week">
                  <table className="accounts cycle list" data-testid="cycle-list" data-cols="cycle-sort">
                    <thead>
                      <tr>
                        {STOP_COLUMNS.map((column) => (
                          <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                        ))}
                      </tr>
                    </thead>
                    {sort && <tbody>{sortRows(shownStops, STOP_COLUMNS, sort).map(stopRow)}</tbody>}
                    {!sort && areas.map((area) => (
                      <tbody key={area}>
                        <tr className="area-row">
                          <th scope="colgroup" colSpan={STOP_COLUMNS.length}>
                            {area}
                          </th>
                        </tr>
                        {shownStops
                          .filter((stop) => stop.area === area)
                          .map((stop) => (
                            stopRow(stop)
                          ))}
                      </tbody>
                    ))}
                  </table>
                </div>
              </section>
            </>
          )}
        </>
      )}
    </main>
  );
}
